import { getCorrelationId } from '@/lib/logging/correlation-id';
import { detectBrowser } from './utils';

/**
 * Client-Side Activity Logger
 * 
 * Captures console logs and user activity during a recording session.
 * Used for interactive feedback and debugging.
 */


export interface LogEntry {
    level: 'log' | 'info' | 'warn' | 'error';
    message: string;
    timestamp: string;
    category?: 'auth' | 'role' | 'access' | 'network' | 'general' | 'ui' | 'api' | 'db' | 'poll' | 'camera';
    correlationId?: string;
}

/** Who started the recording: 'user' = FeedbackButton, 'system' = Auth Debug / auto recording */
export type RecordingSource = 'user' | 'system';

export interface ActivitySession {
    isRecording: boolean;
    logs: LogEntry[];
    userEmail: string | null;
    userId: string | null;
    startTime: string | null;
    endTime: string | null;
    /** Only 'system' triggers auto GitHub issue submission on errors */
    recordingSource: RecordingSource | null;
}

// Max logs to keep in memory
const MAX_LOGS = 500;
const PERSIST_DIAGNOSTIC_EVERY_N_LOGS = 20;
const MAX_MSG_LENGTH = 1000;
const MAX_SERIALIZED_OBJECT_LENGTH = 2000;

// Singleton state
const LOCAL_STORAGE_KEY = 'activity_logs_backup';
export const DIAGNOSTIC_LOGS_PENDING_KEY = 'diagnostic_logs_pending';

// Diagnostic capture mode (background, does NOT show in Feedback button)
let diagnosticCaptureEnabled = false;
let diagnosticBuffer: LogEntry[] = [];
let diagnosticFlushInterval: ReturnType<typeof setInterval> | null = null;

// Singleton state
let state: ActivitySession = {
    isRecording: false,
    logs: [],
    userEmail: null,
    userId: null,
    startTime: null,
    endTime: null,
    recordingSource: null,
};

// Original console methods
const originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
};

let interceptorInitialized = false;

// Categorize log messages
function categorizeLog(message: string): LogEntry['category'] {
    const lowerMessage = message.toLowerCase();

    if (lowerMessage.includes('[auth') || lowerMessage.includes('login') || lowerMessage.includes('firebase')) return 'auth';
    if (lowerMessage.includes('[role') || lowerMessage.includes('permission')) return 'role';
    if (lowerMessage.includes('[access') || lowerMessage.includes('redirect')) return 'access';
    if (lowerMessage.includes('[req]') || lowerMessage.includes('/api/')) return 'network';
    if (lowerMessage.includes('click') || lowerMessage.includes('render')) return 'ui';

    return 'general';
}

/** Redact PII and sensitive data from log messages before persisting to localStorage */
function sanitizeLogForStorage(log: LogEntry): LogEntry {
    let msg = log.message;
    // Redact email-like patterns (keep structure for debugging)
    msg = msg.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[EMAIL_REDACTED]');
    // Redact URLs with sensitive query params
    try {
        msg = msg.replace(/https?:\/\/[^\s]+/g, (url) => sanitizeUrlForStorage(url));
    } catch {
        // Fallback: strip query strings from URLs
        msg = msg.replace(/(https?:\/\/[^\s?]+)\?[^\s]+/g, '$1?[REDACTED]');
    }
    return { ...log, message: msg };
}

function persistLogs() {
    if (typeof window === 'undefined') return;
    try {
        const sanitized = state.logs.map(sanitizeLogForStorage);
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(sanitized));
    } catch (e) {
        // Ignore quota exceeded or other errors
    }
}

function addDiagnosticLog(log: LogEntry) {
    diagnosticBuffer.push(log);
    if (diagnosticBuffer.length > MAX_LOGS) {
        diagnosticBuffer.shift();
    }
    // Throttled persist: every N logs so DevTools shows logs growing during session
    if (diagnosticBuffer.length === 1 || diagnosticBuffer.length % PERSIST_DIAGNOSTIC_EVERY_N_LOGS === 0) {
        persistDiagnosticLogsToStorage();
    }
}

export function captureLog(level: LogEntry['level'], message: string, category: LogEntry['category'] = 'general') {
    const shouldCapture = state.isRecording || diagnosticCaptureEnabled;
    if (!shouldCapture) return;

    // Truncate message if too long
    if (message.length > MAX_MSG_LENGTH) {
        message = message.substring(0, MAX_MSG_LENGTH) + '... [TRUNCATED]';
    }

    const log: LogEntry = {
        level,
        message,
        timestamp: new Date().toISOString(),
        category,
        correlationId: getCorrelationId(),
    };

    if (state.isRecording) {
        state.logs.push(log);
        pendingLogs.push(log);
        notifyListeners(log);
        persistLogs();
        if (state.logs.length > MAX_LOGS) {
            state.logs.shift();
        }
    } else if (diagnosticCaptureEnabled) {
        addDiagnosticLog(log);
    }
}

/**
 * Serialize any value for logging, handling Error objects specially
 * since their properties (message, stack, code) are non-enumerable
 * and JSON.stringify() returns '{}' for them.
 */
export function serializeForLog(arg: any): string {
    if (arg === null || arg === undefined) return String(arg);

    // Handle Error objects specially - properties are non-enumerable
    if (arg instanceof Error) {
        const errorObj: Record<string, any> = {
            name: arg.name,
            message: arg.message,
            stack: arg.stack?.split('\n').slice(0, 3).join('\n'), // Even shorter stack
        };
        // Firebase errors have .code and .customData
        if ('code' in arg) errorObj.code = (arg as any).code;
        if ('customData' in arg) errorObj.customData = (arg as any).customData;
        if ('serverResponse' in arg) errorObj.serverResponse = (arg as any).serverResponse;
        try {
            const str = JSON.stringify(errorObj, null, 2);
            return str.length > MAX_SERIALIZED_OBJECT_LENGTH 
                ? str.substring(0, MAX_SERIALIZED_OBJECT_LENGTH) + '... [OBJECT_TRUNCATED]'
                : str;
        } catch {
            return `Error: ${arg.message} (${arg.name})`;
        }
    }

    if (typeof arg === 'object') {
        try {
            // Avoid logging massive arrays or objects
            if (Array.isArray(arg) && arg.length > 50) {
                return `[Array(${arg.length})] ${JSON.stringify(arg.slice(0, 5))}... [TRUNCATED]`;
            }

            const str = JSON.stringify(arg, null, 2);
            if (str.length > MAX_SERIALIZED_OBJECT_LENGTH) {
                return str.substring(0, MAX_SERIALIZED_OBJECT_LENGTH) + '... [OBJECT_TRUNCATED]';
            }
            return str;
        } catch {
            return String(arg);
        }
    }
    const val = String(arg);
    return val.length > MAX_MSG_LENGTH 
        ? val.substring(0, MAX_MSG_LENGTH) + '... [TRUNCATED]'
        : val;
}

// Add a log entry (from console interceptor)
function addLog(level: LogEntry['level'], args: any[]) {
    const shouldCapture = state.isRecording || diagnosticCaptureEnabled;
    if (!shouldCapture) return;

    let message = args.map(serializeForLog).join(' ');

    // Truncate message if too long
    if (message.length > MAX_MSG_LENGTH) {
        message = message.substring(0, MAX_MSG_LENGTH) + '... [TRUNCATED]';
    }

    const log: LogEntry = {
        level,
        message,
        timestamp: new Date().toISOString(),
        category: categorizeLog(message),
        correlationId: getCorrelationId(),
    };

    if (state.isRecording) {
        state.logs.push(log);
        pendingLogs.push(log);
        notifyListeners(log);
        persistLogs();
        if (state.logs.length > MAX_LOGS) {
            state.logs.shift();
        }
    } else if (diagnosticCaptureEnabled) {
        addDiagnosticLog(log);
    }
}

// Intercept console methods
// NOTE: When the Logger class calls console.*, it sets console._fromLogger = true
// to prevent double-capture (logger already calls captureLog directly).
// The interceptor only captures non-logger console calls (e.g. third-party libraries,
// direct console.* usage, Firebase SDK output).
function initConsoleInterceptor() {
    if (interceptorInitialized) return;
    interceptorInitialized = true;

    console.log = (...args: any[]) => {
        if (!(console as any)._fromLogger) addLog('log', args);
        originalConsole.log.apply(console, args);
    };

    console.info = (...args: any[]) => {
        if (!(console as any)._fromLogger) addLog('info', args);
        originalConsole.info.apply(console, args);
    };

    console.warn = (...args: any[]) => {
        if (!(console as any)._fromLogger) addLog('warn', args);
        originalConsole.warn.apply(console, args);
    };

    console.error = (...args: any[]) => {
        if (!(console as any)._fromLogger) addLog('error', args);
        originalConsole.error.apply(console, args);
    };
}

// Async recovery function - flush recovered logs to server, no GitHub issue creation
async function recoverInterruptedSession(logs: LogEntry[]) {
    if (!logs.length) return;

    // Safety: ensure we don't process very old logs (older than 24h)
    const lastLogTime = new Date(logs[logs.length - 1].timestamp).getTime();
    const now = Date.now();
    if (now - lastLogTime > 24 * 60 * 60 * 1000) return;

    originalConsole.log('🔄 [ActivityLogger] Recovering interrupted session logs...');

    try {
        // Flush logs to server (no GitHub issue creation)
        await flushLogsToServer(logs);
        originalConsole.log('✅ [ActivityLogger] Interrupted session logs flushed to server.');
    } catch (e) {
        originalConsole.error('❌ [ActivityLogger] Failed to recover session:', e);
    } finally {
        // Always clear stored logs to prevent retry loops
        if (typeof window !== 'undefined') {
            try {
                if (state.isRecording) {
                    persistLogs();
                } else {
                    localStorage.removeItem(LOCAL_STORAGE_KEY);
                }
            } catch (storageError) {
                originalConsole.error('❌ [ActivityLogger] Failed to update localStorage:', storageError);
            }
        }
    }
}

// Queue for logs waiting to be sent to server
let pendingLogs: LogEntry[] = [];
let flushInterval: any = null;
const DEFAULT_FLUSH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes default

async function flushLogsToServer(logsToFlush?: LogEntry[]) {
    // If specific logs provided, use those (e.g. recovered logs)
    // Otherwise use pendingLogs
    const logs = logsToFlush || [...pendingLogs];

    if (logs.length === 0) return;

    try {
        // Use /api/diagnostic/logs - allows unauthenticated when diagnostic logging is on
        const response = await fetch('/api/diagnostic/logs', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Correlation-Id': getCorrelationId()
            },
            body: JSON.stringify({ logs })
        });

        if (response.ok) {
            // If we successfully flushed pendingLogs, clear them
            if (!logsToFlush) {
                pendingLogs = [];
            }
        } else {
            // Retrieve 401/other errors to debug if needed, but don't crash
            const text = await response.text();
            // originalConsole.warn(`[ActivityLogger] Failed to flush logs: ${response.status} ${text}`);
        }
    } catch (e) {
        // Network error, keep pending logs for next try
        // originalConsole.error('[ActivityLogger] Network error flushing logs', e);
    }
}

// --- Diagnostic capture (background, no Feedback button UI) ---

async function flushDiagnosticLogsToServer(logsToFlush?: LogEntry[]) {
    const logs = logsToFlush ?? [...diagnosticBuffer];
    if (logs.length === 0) return;

    try {
        const response = await fetch('/api/diagnostic/logs', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Correlation-Id': getCorrelationId()
            },
            body: JSON.stringify({ logs })
        });

        if (response.ok && !logsToFlush) {
            diagnosticBuffer = [];
            if (typeof window !== 'undefined') {
                try {
                    localStorage.removeItem(DIAGNOSTIC_LOGS_PENDING_KEY);
                } catch {
                    // Ignore
                }
            }
        }
    } catch {
        // Network error, keep logs for next try
    }
}

function startDiagnosticFlushInterval() {
    if (diagnosticFlushInterval) clearInterval(diagnosticFlushInterval);
    diagnosticFlushInterval = setInterval(() => flushDiagnosticLogsToServer(), 60 * 1000); // 1 min default
    (async () => {
        try {
            const { getDiagnosticLogFlushIntervalMinutes } = await import('@/components/feedback-button/lib/env-service');
            const minutes = await getDiagnosticLogFlushIntervalMinutes();
            const ms = Math.max(60000, minutes * 60 * 1000);
            if (diagnosticFlushInterval) clearInterval(diagnosticFlushInterval);
            diagnosticFlushInterval = setInterval(() => flushDiagnosticLogsToServer(), ms);
        } catch {
            // Keep default
        }
    })();
}

function persistDiagnosticLogsToStorage() {
    if (typeof window === 'undefined' || diagnosticBuffer.length === 0) return;
    try {
        const sanitized = diagnosticBuffer.map(sanitizeLogForStorage);
        localStorage.setItem(DIAGNOSTIC_LOGS_PENDING_KEY, JSON.stringify(sanitized));
    } catch {
        // Ignore
    }
}

export function isDiagnosticCaptureEnabled(): boolean {
    return diagnosticCaptureEnabled;
}

export function setDiagnosticCaptureEnabled(enabled: boolean) {
    if (diagnosticCaptureEnabled === enabled) return;
    diagnosticCaptureEnabled = enabled;

    if (enabled) {
        initConsoleInterceptor();
        startDiagnosticFlushInterval();
    } else {
        if (diagnosticFlushInterval) {
            clearInterval(diagnosticFlushInterval);
            diagnosticFlushInterval = null;
        }
        if (diagnosticBuffer.length > 0) {
            flushDiagnosticLogsToServer();
        }
    }
}

// Prior-session recovery: on app load, send any pending diagnostic logs from previous session
function flushPriorSessionDiagnosticLogs() {
    if (typeof window === 'undefined') return;
    try {
        const stored = localStorage.getItem(DIAGNOSTIC_LOGS_PENDING_KEY);
        if (!stored) return;
        const logs = JSON.parse(stored) as LogEntry[];
        if (logs.length === 0) return;
        localStorage.removeItem(DIAGNOSTIC_LOGS_PENDING_KEY);
        flushDiagnosticLogsToServer(logs);
    } catch {
        localStorage.removeItem(DIAGNOSTIC_LOGS_PENDING_KEY);
    }
}

// Run prior-session recovery on module load (client-side)
if (typeof window !== 'undefined') {
    flushPriorSessionDiagnosticLogs();

    // Persist diagnostic buffer on unload so it can be sent on next load
    const handleUnload = () => {
        if (diagnosticCaptureEnabled && diagnosticBuffer.length > 0) {
            persistDiagnosticLogsToStorage();
        }
    };
    window.addEventListener('beforeunload', handleUnload);
    window.addEventListener('pagehide', handleUnload);

    // Flush when tab becomes hidden
    document.addEventListener('visibilitychange', () => {
        if (document.hidden && diagnosticCaptureEnabled && diagnosticBuffer.length > 0) {
            flushDiagnosticLogsToServer();
        }
    });
}

export function startRecording(userId?: string, userEmail?: string, source: RecordingSource = 'user') {
    if (state.isRecording) return;

    // Check for interrupted session (recovery)
    if (typeof window !== 'undefined') {
        try {
            const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
            if (stored) {
                const logs = JSON.parse(stored) as LogEntry[];
                if (logs.length > 0) {
                    // Always flush recovered logs to server
                    flushLogsToServer(logs);

                    const currentCorrelationId = getCorrelationId();
                    // Check if last log has DIFFERENT correlation ID
                    const lastLogId = logs[logs.length - 1].correlationId;

                    if (lastLogId && lastLogId !== currentCorrelationId) {
                        // Check if there was an error in the logs
                        const hasError = logs.some(l => l.level === 'error');
                        if (hasError) {
                            // Only trigger GitHub issue if there was an error
                            recoverInterruptedSession(logs);
                        } else {
                            originalConsole.log('🔄 [ActivityLogger] Recovered previous session logs (flushed to server, no error report needed).');
                            // Explicitly clear storage since recoverInterruptedSession won't be called
                            if (typeof window !== 'undefined') {
                                localStorage.removeItem(LOCAL_STORAGE_KEY);
                            }
                        }
                    } else {
                        // Same correlation ID or no ID - likely page reload. 
                        // We flushed them above. Should we clear them?
                        // If it's a reload, we probably want to keep them so the session continues?
                        // BUT `startRecording` resets `state.logs = []`. 
                        // So if we don't clear them from storage, and we don't load them into state... the previous logs are "lost" from the in-memory session but exist in storage?
                        // Actually `persistLogs` writes `state.logs` (which starts empty) to storage, effectively overwriting/clearing the old ones immediately after this function returns and `captureLog` is called.
                        // So we don't strictly need to clear them here if `persistLogs` overwrites.
                        // However, `flushLogs` is async. If we overwrite immediately, we might lose them? 
                        // We already called `flushLogsToServer(logs)` synchronously (invocation) above. `logs` variable holds the copy. so it's fine.
                    }
                }
            }
        } catch (e) {
            // Ignore corrupted storage
        }
    }

    // Reset state
    state = {
        isRecording: true,
        logs: [],
        userEmail: userEmail || null,
        userId: userId || null,
        startTime: new Date().toISOString(),
        endTime: null,
        recordingSource: source,
    };
    pendingLogs = [];

    initConsoleInterceptor();
    const startMsg = `🔴 [ActivityLogger] Recording started. User: ${userId || 'anon'}, Email: ${userEmail || 'none'}`;
    originalConsole.log(startMsg);
    // Capture this system event in the logs so it's not empty
    captureLog('info', startMsg, 'general');

    // NEW: Log client context immediately
    logClientContext();

    // Start auto-flush interval (configurable via diagnosticLogFlushIntervalMinutes)
    if (flushInterval) clearInterval(flushInterval);
    flushInterval = setInterval(() => flushLogsToServer(), DEFAULT_FLUSH_INTERVAL_MS);
    (async () => {
        try {
            const { getDiagnosticLogFlushIntervalMinutes } = await import('@/components/feedback-button/lib/env-service');
            const minutes = await getDiagnosticLogFlushIntervalMinutes();
            const ms = Math.max(60000, minutes * 60 * 1000); // min 1 minute
            if (flushInterval) clearInterval(flushInterval);
            flushInterval = setInterval(() => flushLogsToServer(), ms);
        } catch {
            // Keep default interval on error
        }
    })();
}

export function stopRecording(): LogEntry[] {
    if (!state.isRecording) return state.logs;

    state.isRecording = false;
    state.endTime = new Date().toISOString();
    state.recordingSource = null;

    if (flushInterval) {
        clearInterval(flushInterval);
        flushInterval = null;
    }

    const stopMsg = '⬛ [ActivityLogger] Recording stopped';
    originalConsole.log(stopMsg);
    captureLog('info', stopMsg, 'general');

    originalConsole.log(`   captured ${state.logs.length} logs`);

    // Flush remaining logs
    flushLogsToServer();

    // Clear persisted logs on clean stop
    if (typeof window !== 'undefined') {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
    }

    return [...state.logs];
}

export function isRecording(): boolean {
    return state.isRecording;
}

/** True when recording was started by system-wide auto recording (Auth Debug), not user-triggered */
export function isSystemWideAutoRecording(): boolean {
    return state.isRecording && state.recordingSource === 'system';
}

export function getLogs(): LogEntry[] {
    return [...state.logs];
}

export function setUserInfo(email: string | null, userId: string | null) {
    state.userEmail = email;
    state.userId = userId;
}

export function getSessionInfo() {
    return {
        userEmail: state.userEmail,
        userId: state.userId,
        startTime: state.startTime,
        isRecording: state.isRecording
    };
}

/**
 * Auth Context Capture
 * Captures browser and environment context BEFORE authentication attempt
 * This ensures we have diagnostic info even if auth fails before user identity is established
 */
export interface AuthAttemptContext {
    timestamp: string;
    url: string;
    referrer: string;
    userAgent: string;
    screenSize: string;
    viewport: string;
    cookiesEnabled: boolean;
    language: string;
    timezone: string;
    platform: string;
    authMethod?: 'popup' | 'redirect' | 'email_link' | 'guest';
    attemptNumber?: number;
}

const AUTH_CONTEXT_KEY = 'auth_attempt_context';
const AUTH_ATTEMPT_COUNT_KEY = 'auth_attempt_count';

/** Strip sensitive query params (OAuth codes, tokens) from URLs before storage */
function sanitizeUrlForStorage(url: string): string {
    try {
        const u = new URL(url);
        const sensitiveParams = ['code', 'state', 'oobCode', 'apiKey', 'token', 'access_token', 'id_token'];
        sensitiveParams.forEach(p => u.searchParams.delete(p));
        return u.toString();
    } catch {
        return url.split('?')[0] || url;
    }
}

export function captureAuthAttemptContext(authMethod?: AuthAttemptContext['authMethod']): AuthAttemptContext | null {
    if (typeof window === 'undefined') return null;

    try {
        // Increment attempt counter
        const attemptCount = parseInt(localStorage.getItem(AUTH_ATTEMPT_COUNT_KEY) || '0', 10) + 1;
        localStorage.setItem(AUTH_ATTEMPT_COUNT_KEY, attemptCount.toString());

        const context: AuthAttemptContext = {
            timestamp: new Date().toISOString(),
            url: sanitizeUrlForStorage(window.location.href),
            referrer: document.referrer ? sanitizeUrlForStorage(document.referrer) : 'direct',
            userAgent: navigator.userAgent,
            screenSize: `${window.screen.width}x${window.screen.height}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            cookiesEnabled: navigator.cookieEnabled,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            platform: navigator.platform,
            authMethod,
            attemptNumber: attemptCount,
        };

        // Store context
        localStorage.setItem(AUTH_CONTEXT_KEY, JSON.stringify(context));

        // Log it for capture (sanitize URL to avoid persisting OAuth codes/tokens)
        if (state.isRecording || diagnosticCaptureEnabled) {
            captureLog('info', `[AUTH] Attempt #${attemptCount} started: ${authMethod || 'unknown'}`, 'auth');
            captureLog('info', `[AUTH] Browser: ${navigator.userAgent.substring(0, 100)}`, 'auth');
            captureLog('info', `[AUTH] URL: ${sanitizeUrlForStorage(window.location.href)}`, 'auth');
            captureLog('info', `[AUTH] Cookies: ${navigator.cookieEnabled ? 'enabled' : 'DISABLED'}`, 'auth');
        }

        originalConsole.log('🔐 [AuthContext] Captured attempt context:', context);
        return context;
    } catch (error) {
        originalConsole.error('[AuthContext] Failed to capture context:', error);
        return null;
    }
}

export function getAuthAttemptContext(): AuthAttemptContext | null {
    if (typeof window === 'undefined') return null;

    try {
        const stored = localStorage.getItem(AUTH_CONTEXT_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (error) {
        return null;
    }
}

export function clearAuthAttemptContext() {
    if (typeof window === 'undefined') return;

    try {
        localStorage.removeItem(AUTH_CONTEXT_KEY);
        localStorage.removeItem(AUTH_ATTEMPT_COUNT_KEY);
        originalConsole.log('🔐 [AuthContext] Cleared attempt context');
    } catch (error) {
        // Ignore errors
    }
}

export function getAuthAttemptCount(): number {
    if (typeof window === 'undefined') return 0;

    try {
        return parseInt(localStorage.getItem(AUTH_ATTEMPT_COUNT_KEY) || '0', 10);
    } catch {
        return 0;
    }
}

/**
 * Logs data about the client (browser, OS, etc.) 
 * to the currently active recording session.
 */
function logClientContext() {
    if (typeof window === 'undefined' || !state.isRecording) return;

    try {
        const browser = detectBrowser();
        const ua = navigator.userAgent;
        const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
        const isIOS = /iPhone|iPad|iPod/i.test(ua);
        const isInAppBrowser = /FBAN|FBAV|Instagram|Twitter|Line|WeChat|Snapchat/i.test(ua);

        captureLog('info', `[Client] URL: ${sanitizeUrlForStorage(window.location.href)}`, 'ui');
        captureLog('info', `[Client] Referrer: ${document.referrer || 'direct'}`, 'ui');
        captureLog('info', `[Client] Browser: ${browser.name} ${browser.version}`, 'ui');
        captureLog('info', `[Client] Device: ${isMobile ? 'Mobile' : 'Desktop'}${isIOS ? ' (iOS)' : ''}${isInAppBrowser ? ' (In-App Browser)' : ''}`, 'ui');
        const screenWidth = window.screen?.width ?? 0;
        const screenHeight = window.screen?.height ?? 0;
        const innerWidth = window.innerWidth ?? 0;
        const innerHeight = window.innerHeight ?? 0;
        captureLog('info', `[Client] Screen: ${screenWidth}x${screenHeight} (Viewport: ${innerWidth}x${innerHeight})`, 'ui');
        captureLog('info', `[Client] Language: ${navigator.language}, Timezone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`, 'ui');
        captureLog('info', `[Client] Cookies: ${navigator.cookieEnabled ? 'Enabled' : 'DISABLED'}`, 'ui');
        captureLog('info', `[Client] Platform: ${navigator.platform}`, 'ui');
    } catch (e) {
        originalConsole.error('[ActivityLogger] Failed to log client context:', e);
    }
}

// Expose on window for debugging ONLY in development (reduces XSS exfiltration surface)
if (typeof window !== 'undefined' && import.meta.env?.DEV) {
    (window as any).__activityLogger = {
        startRecording,
        stopRecording,
        getLogs: () => getLogs().map(sanitizeLogForStorage), // Return sanitized logs only
        getSessionInfo, // Minimal session info, no raw state
    };
}

// Prevent duplicate auto-submissions (exported for AuthDebugMonitor to skip redundant handling)
let errorReportInProgress = false;
export function isErrorReportInProgress(): boolean {
    return errorReportInProgress;
}
export function setErrorReportInProgress(value: boolean): void {
    errorReportInProgress = value;
}

// Circuit breaker for auto-reporting
type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';
interface CircuitBreakerState {
    state: CircuitState;
    failureCount: number;
    lastFailureTime: number;
    cooldownUntil: number;
}

const circuitBreaker: CircuitBreakerState = {
    state: 'CLOSED',
    failureCount: 0,
    lastFailureTime: 0,
    cooldownUntil: 0,
};

// Circuit breaker configuration
const CIRCUIT_FAILURE_THRESHOLD = 3; // Open circuit after 3 failures
const CIRCUIT_COOLDOWN_MS = 5 * 60 * 1000; // 5 minutes cooldown
const CIRCUIT_HALF_OPEN_ATTEMPTS = 1; // Reset to CLOSED after 1 success in HALF_OPEN

function checkCircuitBreaker(): boolean {
    const now = Date.now();

    if (circuitBreaker.state === 'OPEN') {
        // Check if cooldown period has elapsed
        if (now >= circuitBreaker.cooldownUntil) {
            originalConsole.log('🔄 [ActivityLogger] Circuit breaker entering HALF_OPEN state (cooldown complete)');
            circuitBreaker.state = 'HALF_OPEN';
            return true; // Allow attempt
        }
        const remainingMs = circuitBreaker.cooldownUntil - now;
        originalConsole.warn(`⚡ [ActivityLogger] Circuit breaker OPEN - ${Math.ceil(remainingMs / 1000)}s remaining`);
        return false; // Circuit is open, reject
    }

    return true; // CLOSED or HALF_OPEN - allow attempt
}

function recordCircuitSuccess(): void {
    if (circuitBreaker.state === 'HALF_OPEN') {
        originalConsole.log('✅ [ActivityLogger] Circuit breaker closing (success in HALF_OPEN)');
        circuitBreaker.state = 'CLOSED';
        circuitBreaker.failureCount = 0;
    } else if (circuitBreaker.state === 'CLOSED') {
        // Reset failure count on success
        circuitBreaker.failureCount = 0;
    }
}

function recordCircuitFailure(): void {
    circuitBreaker.failureCount++;
    circuitBreaker.lastFailureTime = Date.now();

    if (circuitBreaker.state === 'HALF_OPEN') {
        // Failed in HALF_OPEN, reopen circuit
        originalConsole.warn('❌ [ActivityLogger] Circuit breaker reopening (failure in HALF_OPEN)');
        circuitBreaker.state = 'OPEN';
        circuitBreaker.cooldownUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
    } else if (circuitBreaker.failureCount >= CIRCUIT_FAILURE_THRESHOLD) {
        // Too many failures, open circuit
        originalConsole.error(`❌ [ActivityLogger] Circuit breaker opening (${circuitBreaker.failureCount} consecutive failures)`);
        circuitBreaker.state = 'OPEN';
        circuitBreaker.cooldownUntil = Date.now() + CIRCUIT_COOLDOWN_MS;
    }
}

// Rate limit with exponential backoff
let lastErrorReportTime = 0;
const BASE_RATE_LIMIT_MS = 60 * 1000; // 1 minute base
const MAX_RATE_LIMIT_MS = 15 * 60 * 1000; // 15 minutes max
let consecutiveRateLimitedAttempts = 0;

function getRateLimitDelay(): number {
    // Exponential backoff: base * 2^attempts, capped at max
    const delay = Math.min(
        BASE_RATE_LIMIT_MS * Math.pow(2, consecutiveRateLimitedAttempts),
        MAX_RATE_LIMIT_MS
    );
    return delay;
}

function checkRateLimit(): boolean {
    const now = Date.now();
    const requiredDelay = getRateLimitDelay();
    const timeSinceLastReport = now - lastErrorReportTime;

    if (timeSinceLastReport < requiredDelay) {
        consecutiveRateLimitedAttempts++;
        const remainingMs = requiredDelay - timeSinceLastReport;
        originalConsole.warn(`⏱️ [ActivityLogger] Rate limited - ${Math.ceil(remainingMs / 1000)}s remaining (backoff: ${Math.ceil(requiredDelay / 1000)}s)`);
        return false;
    }

    // Reset backoff on successful timing
    consecutiveRateLimitedAttempts = 0;
    return true;
}

/**
 * When system-wide auto recording is active and an error is logged.
 * Automatic GitHub issue creation has been removed - logs are buffered and flushed.
 * Users can create issues manually via the Feedback button.
 */
export function triggerErrorReportIfSystemWide(): void {
    // No-op: automatic GitHub issue creation removed
}

// Log listeners
type LogListener = (log: LogEntry) => void;
const listeners: LogListener[] = [];

export function subscribe(listener: LogListener) {
    listeners.push(listener);
    return () => {
        const index = listeners.indexOf(listener);
        if (index > -1) {
            listeners.splice(index, 1);
        }
    };
}

function notifyListeners(log: LogEntry) {
    listeners.forEach(listener => {
        try {
            listener(log);
        } catch (err) {
            console.error('Error in log listener:', err);
        }
    });
}
